import { ApplicationConfig, provideZoneChangeDetection, APP_INITIALIZER, DEFAULT_CURRENCY_CODE, provideAppInitializer, inject, LOCALE_ID } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, provideRouter, RouteReuseStrategy, withDebugTracing, withInMemoryScrolling } from '@angular/router';

import { routes } from './app.routes';
import { provideClientHydration, withEventReplay, withIncrementalHydration } from '@angular/platform-browser';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideNgxStripe } from 'ngx-stripe';
import { InitAppService } from '@core/services/config/init-app.service';
import { Log } from '@core/services/config/app-config.service';
import { Observable } from 'rxjs';
import { apiCacheInterceptor } from '@core/interceptors/api-cache.interceptor';
import { initializeUserStore } from '@core/services/storage/user/user-store.service';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideToastr } from 'ngx-toastr';

import { withComponentInputBinding } from '@angular/router';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';

import { environment } from '@env/environment';
import { initializeTracking } from '@core/services/config/tracking.service';
import { trackingInterceptor } from '@core/interceptors/tracking.interceptor';

function initApp(): Promise<unknown> {
    const initAppService = inject(InitAppService);
    return initAppService.InitWpromise();
}

function localizeApp(): Promise<string> {
    const initAppService = inject(InitAppService);
    return initAppService.LocalizeWpromise();
}

// Factory function per LOCALE_ID
function getLocaleId(): string {
    const initAppService = inject(InitAppService);
    return initAppService.localeId;
}

// Factory function per DEFAULT_CURRENCY_CODE
function getCurrencyCode(): string {
    const initAppService = inject(InitAppService);
    return initAppService.currencyCode;
}

var storage_cache: Map<string, DetachedRouteHandle>;
export function logStorage() {
    if (storage_cache) {
    }
}

export function emptyStorage() {
    if (storage_cache) {
        storage_cache.clear();
    }
}

/***************************************** route strategy *****************************************/
export class SimplifiedReuseStrategy implements RouteReuseStrategy {
    shouldDetach(): boolean {
        return false;
    }

    store(): void {
        // Non fa nulla
    }

    shouldAttach(): boolean {
        return false;
    }

    retrieve(): DetachedRouteHandle | null {
        return null;
    }

    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        return future.routeConfig === curr.routeConfig &&
            JSON.stringify(future.params) === JSON.stringify(curr.params);
    }
}
/***************************************** //route strategy *****************************************/
async function initializeAll(): Promise<void> {
    const initAppService = inject(InitAppService);
    
    // 1. Prima inizializza l'app (sync, setta dominio)
    await initAppService.InitWpromise();
    
    // 2. Poi carica le traduzioni (async, carica JSON)
    await initAppService.LocalizeWpromise();
    
    // Ora Globals ha le traduzioni, qualsiasi servizio può essere creato
}

export const appConfig: ApplicationConfig = {
    providers: [
        // LCP: animations engine caricato on-demand (fuori dal bundle eager) → -58K vendor sul critical path
        provideAnimationsAsync(),

        provideToastr({
            timeOut: 3000,
            positionClass: 'toast-center-center',
            preventDuplicates: true,
            progressBar: true,
        }),

        // Un solo initializer che fa tutto in ordine
        provideAppInitializer(initializeAll),
        
        // Questi vengono dopo, quando le traduzioni sono pronte
        provideAppInitializer(initializeTracking),
        provideAppInitializer(initializeUserStore),
        
          // Stripe
        provideNgxStripe(environment.stripe_publishable_key),
        
        // LOCALE_ID provider dinamico
        {
            provide: LOCALE_ID,
            useFactory: getLocaleId,
            deps: [InitAppService]
        },
        
        // Currency provider dinamico
        // il default viene usato quando si usa la pipe senza parametri <value> | currency
        // quindi và impostato dinamicamente
        {
            provide: DEFAULT_CURRENCY_CODE,
            useFactory: getCurrencyCode,
            deps: [InitAppService]
        },

        {provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: {dateFormat: 'shortDate'}},

        provideZoneChangeDetection({ eventCoalescing: true }),

        provideRouter(
            routes,
            withInMemoryScrolling({
                scrollPositionRestoration: "top",
            }),
            withComponentInputBinding()
        ),
        
        { provide: RouteReuseStrategy, useClass: SimplifiedReuseStrategy },

        provideHttpClient(
            withFetch(),
            withInterceptors([
                apiCacheInterceptor,
                // aggiungere l'auth interceptor
                authInterceptor,
                trackingInterceptor
            ]),
        ),

        provideClientHydration(withIncrementalHydration(), withEventReplay()),
    ]
};